import { Stack } from '@mui/material';
import { FC, PropsWithChildren } from 'react';

import { Permission } from '@/api/permissions';
import { theme, Typography } from '@/shared/design-system';
import { GeneralLoadingFallback } from '@/shared/modules/general-loading-fallback';

import { usePermissions } from './use-permissions';

export const DEFAULT_ACCESS_DENIED_MESSAGE = "You don't have access to this section";

export type RequirePermissionInlineProps = PropsWithChildren<{
  /** Permission the session must hold to see `children`. */
  permission: Permission;
  /** Shown in place of `children` when the permission is absent. */
  deniedMessage?: string;
}>;

/**
 * Section/tab-level permission gate (GP-3853 Task 11 fix round 1) — unlike
 * `RequirePermission`, this never navigates. `RequirePermission` is a ROUTE
 * gate: denial means the whole route shouldn't have been reachable, so
 * bouncing to the app root and toasting is correct there. Used to gate a tab
 * or section *inside* an otherwise-reachable page, that same redirect would
 * incorrectly evict the caller from the whole page over one sub-section they
 * merely lack a narrower permission for (e.g. a `GammaTeamView` holder
 * who lacks `GammaViewRoles` should still see the rest of
 * `gamma-team-management`, just not the Roles tab).
 *
 * Renders the loading fallback while the profile resolves (deny-by-default:
 * a denial is only real once `isLoading` is false), a quiet inline panel on
 * denial, and `children` once the permission is confirmed held. UX affordance
 * only — the backend enforces the same permission independently.
 */
export const RequirePermissionInline: FC<RequirePermissionInlineProps> = ({
  permission,
  deniedMessage = DEFAULT_ACCESS_DENIED_MESSAGE,
  children,
}) => {
  const { has, isLoading } = usePermissions();

  if (isLoading) {
    return <GeneralLoadingFallback />;
  }

  if (!has(permission)) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="8px"
        padding="40px 16px"
        textAlign="center"
      >
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {deniedMessage}
        </Typography>
      </Stack>
    );
  }

  return <>{children}</>;
};
